import React, { useState, useEffect } from 'react';
import { devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import AuctionCard from '../../customComponents/AuctionCard';
import ImageView from '../../components/ImageView';
import DataLoader from '../../components/DataLoader';
import { useLang } from '../../context/LangContext';
import { getProcessingAuctions } from '../../lib/api/user/userApi';

const OngoingAuctionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1; /* 상위 컨테이너의 남은 공간을 차지 */
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  padding: 0 0 80px 0; /* 모바일에서는 좌우 패딩 제거, 하단만 바텀 네비게이션과의 간격 확보 */
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 24px 32px 80px 32px; /* 웹에서는 더 큰 패딩 */
  }
  
  @media (min-width: 1024px) {
    gap: 32px;
    padding: 32px 40px 80px 40px; /* 데스크톱에서는 더 큰 패딩 */
  }
`;

// 웹용 제목 컴포넌트 (FormTitle과 동일한 스타일)
const WebTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 20px 20px 20px 20px; /* 웹에서만 좌우 여백 추가 */
  grid-column: 1 / -1; /* 그리드 전체 너비를 차지 */
  
  @media (max-width: 767px) {
    display: none; /* 모바일에서는 숨김 */
  }
`;

// 노데이터 래퍼 (최소 높이를 보장하는 컨테이너)
const NoDataWrapper = styled.div`
  min-height: calc(100vh - 200px); /* 앱바와 여백을 고려한 최소 높이 */
  display: flex;
  flex-direction: column;
`;

// 노데이터 컨테이너 (앱바를 제외한 높이로 중앙 정렬)
const NoDataContainer = styled.div`
  flex: 1; /* 부모 컨테이너의 남은 공간을 모두 차지 */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  
  @media (max-width: 767px) {
    padding: 0; /* 모바일에서는 패딩 제거 */
  }
`;

// 다국어 텍스트 객체
const ongoingAuctionsTexts = {
  ko: {
    title: '진행중인 경매',
    preparing: '경매 준비중입니다'
  },
  en: {
    title: 'Ongoing Auctions',
    preparing: 'Preparing for auction'
  }
};

const OngoingAuctions: React.FC = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const texts = ongoingAuctionsTexts[lang];

  // 현재 URL을 기반으로 currentPage 결정
  const currentPage = window.location.pathname === '/live-auctions' ? 'live-auctions' : undefined;
  
  // 상태 관리
  const [auctions, setAuctions] = useState<any[]>([]); // 임시로 any 타입 사용
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // API 호출
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getProcessingAuctions({ page: 1 });
        
        // API 응답이 res.json 형태로 올 때 처리
        let auctionsData = [];
        
        // response 자체가 배열인 경우 (직접 경매 데이터)
        if (Array.isArray(response)) {
          auctionsData = response;
        }
        // response.data가 배열인 경우
        else if (Array.isArray(response?.data)) {
          auctionsData = response.data;
        }
        // response.data.data.auctions가 있는 경우 (기존 구조)
        else if ((response as any)?.data?.data?.auctions) {
          auctionsData = (response as any).data.data.auctions;
        }
        // response.data.auctions가 있는 경우
        else if (response?.data?.auctions) {
          auctionsData = response.data.auctions;
        }
        
        setAuctions(auctionsData);
      } catch (err) {
        devError('Failed to fetch processing auctions:', err);
        setError('경매 정보를 불러오는데 실패했습니다.');
        setAuctions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuctions();
  }, []);

  const handleCardClick = (auction: any) => {
    // 경매에 있는 모든 lots를 기반으로 lot detail 페이지로 이동
    if (auction && auction.index) {
      // 경매의 모든 lots를 auctionItems 형태로 변환
      const auctionItems = auction.lots?.map((lot: any) => {
        // 날짜 포맷 함수 (yy.mm.dd hh:MM 형태)
        const formatDate = (date: Date): string => {
          const year = date.getFullYear().toString().slice(-2);
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${year}.${month}.${day} ${hours}:${minutes}`;
        };

        const now = new Date();
        const auctionStartAt = new Date(auction.auctionStartAt);
        const auctionEndAt = new Date(auction.auctionEndAt);

        // 경매 상태 판별
        const isEnded = now > auctionEndAt;
        const isLive = now >= auctionStartAt && now <= auctionEndAt;
        
        // 이미지 URL 구성 (첫 번째 이미지 사용)
        const imageUrl = lot.images?.length > 0 ? 
          (lot.images[0].startsWith('http') ? 
            lot.images[0] : 
            `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${lot.images[0]}`) : 
          '';

        return {
          id: lot.index,
          imageUrl: imageUrl,
          auctionHouse: auction.auctioneer?.name || '경매사',
          auction: auction.name,
          lotName: lot.name,
          startingPrice: lot.startPrice?.toLocaleString() || '0',
          startingPriceUsd: lot.startPriceUsd,
          exchangeRate: lot.exchangeRate,
          liveTime: auctionStartAt.toISOString(),
          liveEndTime: auctionEndAt.toISOString(),
          isLive: isLive,
          photoSubmissionDate: formatDate(new Date(auction.preBidStartAt)),
          auctionStartDate: formatDate(auctionStartAt),
          auctionEndDate: formatDate(auctionEndAt),
          isFavorite: lot.likeCount > 0,
          isEnded: isEnded,
          likedUserIndexes: lot.likedUserIndexes || [],
          apiData: {
            ...lot,
            auction: auction,
            auctioneer: auction.auctioneer
          }
        };
      }) || [];

      // 첫 번째 lot을 선택된 아이템으로 설정
      const selectedItem = auctionItems[0];

      if (selectedItem) {
        // lot-detail 페이지로 이동
        navigate(`/lot-detail/${auction.index}`, { 
          state: { 
            auction: auction.name,
            auctionHouse: auction.auctioneer?.name || '경매사',
            items: auctionItems,
            exchangeRate: selectedItem.exchangeRate || 1300,
            selectedItem: selectedItem,
            selectedLotId: selectedItem.id
          } 
        });
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // 모바일 환경 감지
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 로딩 중이거나 데이터가 없을 때의 처리
  if (loading) {
    return (
      <MainLayout currentPage={currentPage}>
        <CenteredLayout>
          <ProjectAppbar 
            title={texts.title}
            showBackButton={!isMobile}
            onBack={handleBack}
          />
          <OngoingAuctionsContainer>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 120px)',
              width: '100%'
            }}>
              <DataLoader />
            </div>
          </OngoingAuctionsContainer>
        </CenteredLayout>
      </MainLayout>
    );
  }

  if (error || !auctions || auctions.length === 0) {
    return (
      <MainLayout currentPage={currentPage}>
        <CenteredLayout>
          <ProjectAppbar 
            title={texts.title}
            showBackButton={!isMobile}
            onBack={handleBack}
          />
          <OngoingAuctionsContainer>
            <NoDataWrapper>
              <NoDataContainer>
                <ImageView
                  imageUrl="/common/nodata.svg"
                  width={107}
                  height={107}
                  alt="No data"
                />
                <div style={{ 
                  marginTop: '20px',
                  color: '#949494',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {texts.preparing}
                </div>
              </NoDataContainer>
            </NoDataWrapper>
          </OngoingAuctionsContainer>
        </CenteredLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout currentPage={currentPage}>
      <CenteredLayout>
        <ProjectAppbar 
          title={texts.title}
          showBackButton={!isMobile}
          onBack={handleBack}
        />
        <OngoingAuctionsContainer>
          <ContentContainer>
            <WebTitle>{texts.title}</WebTitle>
            {auctions?.map((auction) => (
              <AuctionCard 
                key={auction.index}
                auction={auction}
                lang={lang}
                type="ongoing"
                onClick={handleCardClick}
              />
            ))}
            </ContentContainer>
          </OngoingAuctionsContainer>
        </CenteredLayout>
      </MainLayout>
    );
};

export default OngoingAuctions;
